import { Injectable, inject } from '@angular/core';
import type { IpeHistoryTileData, IpeTileData } from '../../interfaces/Tile.interface';
import type { CustomTileProvider } from './providers/custom-tile.provider';
import { TileAnalysisGraphProvider } from './providers/tile-analysis-graph.provider';
import { TileComfortProvider } from './providers/tile-comfort.provider';
import { TileIpeGaugeProvider } from './providers/tile-ipe-gauge.provider';
import { TileIpeValueProvider } from './providers/tile-ipe-value.provider';
import { TileMultiSensorsProvider } from './providers/tile-multi-sensors.provider';
import { TileMultiStateSensorsProvider } from './providers/tile-multi-state-sensors.provider';
import { TileTypicalWeeksProvider } from './providers/tile-typical-weeks.provider';
import { TileConstants } from './tiles.constants';

@Injectable({
    providedIn: 'root'
})
export class TilesStaticProvider {
    private tileMultiStateSensorsProvider = inject(TileMultiStateSensorsProvider);
    private tileMultiSensorsProvider = inject(TileMultiSensorsProvider);
    private tileIpeValueProvider = inject(TileIpeValueProvider);
    private tileIpeGaugeProvider = inject(TileIpeGaugeProvider);
    private tileAnalysisGraphProvider = inject(TileAnalysisGraphProvider);
    private tileTypicalWeeksProvider = inject(TileTypicalWeeksProvider);
    private tileComfortProvider = inject(TileComfortProvider);

    getTileProvider(tileType: string): CustomTileProvider | undefined {
        switch (tileType) {
            case TileConstants.IPE_VALUE:
            case TileConstants.IPE_VALUE_VARIABLE_INDICATOR:
                return this.tileIpeValueProvider;
            case TileConstants.IPE_GAUGE:
            case TileConstants.IPE_GAUGE_VARIABLE_INDICATOR:
                return this.tileIpeGaugeProvider;
            case TileConstants.customTiles.MULTI_SENSORS:
                return this.tileMultiSensorsProvider;
            case TileConstants.customTiles.MULTI_STATE_SENSORS:
                return this.tileMultiStateSensorsProvider;
            case TileConstants.customTiles.ANALYSIS_GRAPH:
                return this.tileAnalysisGraphProvider;
            case TileConstants.customTiles.TYPICAL_WEEK:
                return this.tileTypicalWeeksProvider;
            case TileConstants.customTiles.COMFORT:
                return this.tileComfortProvider;
        }
        console.error(`There is no spawnable tile with type ${tileType}`);
    }

    // The HDD and CDD indicators require tile type suffixed by _VARIABLE_INDICATOR
    // (ex: IPE_GAUGE becomes IPE_GAUGE_VARIABLE_INDICATOR)
    static getIpeRealTypeUsingIndicator(tile: IpeTileData | IpeHistoryTileData): string {
        if (
            tile.description &&
            (tile.description.indicator === 'HDD18' || tile.description.indicator === 'CDD15') &&
            !tile.tileType.endsWith('_VARIABLE_INDICATOR')
        ) {
            return tile.tileType + '_VARIABLE_INDICATOR';
        } else if (
            tile.description &&
            tile.description.indicator !== 'HDD18' &&
            tile.description.indicator !== 'CDD15' &&
            tile.tileType.endsWith('_VARIABLE_INDICATOR')
        ) {
            return tile.tileType.replace('_VARIABLE_INDICATOR', '');
        } else {
            return tile.tileType;
        }
    }

    static isIpeValue(tile: IpeTileData | IpeHistoryTileData): boolean {
        if (!tile) {
            return false;
        }
        return tile.tileType === TileConstants.IPE_VALUE || tile.tileType === TileConstants.IPE_VALUE_VARIABLE_INDICATOR;
    }

    static isIpeGauge(tile: IpeTileData | IpeHistoryTileData): boolean {
        if (!tile) {
            return false;
        }
        return tile.tileType === TileConstants.IPE_GAUGE || tile.tileType === TileConstants.IPE_GAUGE_VARIABLE_INDICATOR;
    }
}
